// SPDX-License-Identifier: MIT
// Cloud-save orchestration for the desktop code-continuity flow. After each
// agent run (and on a manual "save now"), the built code is pushed to the
// project's Forgejo repo so it follows the user across workstations. This module
// is the HONEST, NON-BLOCKING brain: it mints a per-repo write token, drives the
// push, and maps the raw push outcome to a UI state. It NEVER throws — a failure
// surfaces as an `unsaved` state, the build is never blocked, no work is lost.
import type { PushStatus } from './code-push';

// The honest UI state machine of the cloud-save indicator (the visual component
// is built separately via /frontend-design ; this is the contract it renders).
export type CloudSaveState = 'idle' | 'saving' | 'saved' | 'unsaved' | 'diverged';

/** Map a push status to the indicator state. `diverged` is a warning (never an
 *  overwrite) ; everything that isn't a clean save / divergence is `unsaved`. */
export function pushStatusToCloudState(status: PushStatus): CloudSaveState {
  if (status === 'pushed' || status === 'nothing-to-commit' || status === 'remote-ahead-pulled') return 'saved';
  if (status === 'diverged') return 'diverged';
  return 'unsaved';
}

// Injected I/O so the orchestration is unit-testable without Tauri / network.
export interface CloudSaveDeps {
  mint: (projectId: string) => Promise<string | null>;
  push: (dir: string, repoUrl: string, token: string, message: string) => Promise<{ status: PushStatus; detail: string }>;
  now: () => string;
}

export interface CloudSaveArgs {
  projectDir: string;
  projectId: string;
  repoUrl: string;
}

// Run one cloud-save cycle. Emits `saving` immediately, then the resolved state.
// Resolves with the final state ; never rejects (any error → `unsaved`).
export async function runCloudSave(
  args: CloudSaveArgs,
  emit: (state: CloudSaveState) => void,
  deps: CloudSaveDeps,
): Promise<CloudSaveState> {
  emit('saving');
  try {
    const token = await deps.mint(args.projectId);
    if (!token) { emit('unsaved'); return 'unsaved'; }
    const out = await deps.push(args.projectDir, args.repoUrl, token, `Genesis: build ${deps.now()}`);
    const state = pushStatusToCloudState(out.status);
    emit(state);
    return state;
  } catch {
    emit('unsaved');
    return 'unsaved';
  }
}
